import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Loader2, MessageSquare, Inbox } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

const EmptyTickets = () => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <Inbox className="h-8 w-8 text-muted-foreground mb-3" />
    <p className="text-sm font-medium">No tickets yet</p>
    <p className="text-xs text-muted-foreground mt-1">
      Reports and feature requests you submit will appear here.
    </p>
  </div>
);

interface Ticket {
  id: string;
  ticket_number: number;
  type: string;
  severity: string | null;
  title: string;
  status: "open" | "in_review" | "resolved" | "wont_fix";
  created_at: string;
}

const STATUS_LABEL: Record<Ticket["status"], string> = {
  open: "Open",
  in_review: "In review",
  resolved: "Resolved",
  wont_fix: "Won't fix",
};

const STATUS_COLOR: Record<Ticket["status"], string> = {
  open: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  in_review: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  resolved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  wont_fix: "bg-muted text-muted-foreground",
};

const TYPE_LABEL: Record<string, string> = {
  bug: "Bug",
  feature: "Feature",
  question: "Question",
};

export function MyTicketsTab() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setTickets([]);
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("support_tickets")
        .select("id, ticket_number, type, severity, title, status, created_at")
        .order("created_at", { ascending: false })
        .limit(50);
      if (cancelled) return;
      if (!error && data) setTickets(data as Ticket[]);
      else setTickets([]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!tickets || tickets.length === 0) {
    return <EmptyTickets />;
  }

  return (
    <ul className="space-y-2">
      {tickets.map((t) => (
        <li
          key={t.id}
          className="border border-border/40 rounded-lg p-3 hover:bg-accent/30 transition-colors"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[11px] text-muted-foreground">
                  FC-{t.ticket_number}
                </span>
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  {TYPE_LABEL[t.type] || t.type}
                </span>
              </div>
              <p className="text-sm font-medium mt-1 line-clamp-2">{t.title}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
                <MessageSquare className="h-3 w-3" />
                {format(new Date(t.created_at), "MMM d, yyyy")}
              </p>
            </div>
            <span
              className={cn(
                "text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold shrink-0",
                STATUS_COLOR[t.status]
              )}
            >
              {STATUS_LABEL[t.status]}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}
